import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadSettings, saveSettings } from './settingsStore';
import SearchableSelect from '../../components/SearchableSelect';
import OpenFilterLink from '../../components/OpenFilterLink';
import BomFilterCheck from './BomFilterCheck';
import { getCsrfToken } from '../../utils/auth';
import './BomFilterCheck.css';
import './BomSettingsForm.css';

// BOM Settings — the saved Orcanos filters this TENANT's BOM viewer reads.
//
// Lives in Admin → Settings (it was a ⚙ modal on the BOM page, and before that a read-only
// view of the standalone app's `settings.xml`). The row is per tenant on the server; the
// page that hosts this is admin-only, but the server re-checks on save — hiding the form is
// not the control (Critical Note #20).
//
// Loads its own copy of the settings rather than reading the BOM module's store: the admin
// may open Settings without ever having visited the BOM page in this tab.
//
// The project/version comes from QW_Login (`/api/config/projects`) and each filter from
// GetFilterList (`/api/config/filters`) — the same pickers as the Training config, with the
// same fallback to a plain id box when the tenant has no filter list.
//
// `image_url_query` IS shown (v3.48.1). It was hidden for a while, which made a whole tenant's
// Image column silently degrade to filename links with nothing in the UI to explain it: an
// Orcanos attachment under `<tenant>FTP` is served only to a session cookie, so without this
// token the browser's <img> request is denied and sanitizeHtml's fallback swaps in a link.
// Empty is legitimate (a tenant whose images are all external URLs), so it is never required.

// Only a version id is stored, so the project is found by the version it owns.
const projectLabel = (p) => `${p.project_name}${p.version_label ? ` (${p.version_label})` : ''}`;

// `only` lists the BOM structures a field belongs to (absent = every structure).
// `check` is the kind the column check asks about (see BomFilterCheck / bom_fields.json).
const FILTERS = [
  { key: 'bom_filter_id', label: 'BOM filter', code: 'PRT', hint: 'Top-level BOMs (PRT)',
    required: true, only: ['instance', 'hierarchy', 'none'] },
  { key: 'instance_filter_id', label: 'Part Instance filter', code: 'PI', hint: 'Children of a BOM (PI)',
    only: ['instance'] },
  // The assembly structure's two levels. Two DIFFERENT work item types — the tenant's own
  // BOM type at the top, its Assembly type below — so each carries its own type picker,
  // and both read from the assembly project rather than the parts project.
  { key: 'assembly_root_filter_id', label: 'Top-level BOM filter', only: ['assembly'], required: true,
    typeKey: 'assembly_root_type', typeLabel: 'Top-level BOM work item',
    labelKey: 'assembly_root_label', check: 'assembly_root',
    versionKey: 'assembly_version_id', versionLabel: 'Assembly project version',
    versionHint: 'The project that holds the BOM and Assembly work items. Empty = the parts project.',
    hint: 'The BOMs at the top of the tree. Everything below is read from the sub-assembly and Part Catalog filters.' },
  { key: 'assembly_filter_id', label: 'Sub-assembly filter', only: ['assembly'],
    typeKey: 'assembly_type', typeLabel: 'Assembly work item',
    labelKey: 'assembly_label', check: 'assembly',
    assemblyVersion: true,
    hint: 'Sub-assemblies, read for every node with parent_original_id = <id>. Must select that column, or nothing below the top level ever appears.' },
  { key: 'part_catalog_filter_id', label: 'Part Catalog filter', code: 'PRT', hint: 'The Part Catalog view (PRT)',
    check: 'parts',
    hierarchyHint: 'The Part Catalog view (PRT) — and the parts every BOM row expands into',
    assemblyHint: 'The Part Catalog view (PRT) — and the parts at the bottom of every assembly' },
  // Change Requests live in their own project (e.g. QMS E-forms), so the ECO filter has its
  // own project picker. `eco_version_id` empty = the parts project.
  { key: 'related_eco_filter_id', label: 'Related ECO filter', code: 'CR', hint: 'Change Requests (CR)', versionKey: 'eco_version_id',
    versionLabel: 'ECO project version', versionHint: 'The project that holds Change Requests, e.g. QMS E-forms.' },
  // DMS items that act as parts — their own list in the BOM menu, and an ECO's documents.
  { key: 'dms_filter_id', label: 'DMS items filter', code: 'DMS', hint: 'Documents used as parts (DMS). Set = "Parts - DMS items" appears in the BOM menu, and under the assembly structure documents appear inside their assembly (needs parent_original_id).',
    check: 'dms',
    versionKey: 'dms_version_id', versionLabel: 'DMS project version', versionHint: 'The project that holds the DMS items, e.g. Document Control.' },
  // ECO management — the list of ECOs; each expands into its parts and DMS items.
  { key: 'eco_filter_id', label: 'ECO list filter', code: 'CR', hint: 'The ECOs to manage (CR). Set = the ECO list appears in the BOM menu, named after the work item.',
    versionKey: 'eco_list_version_id', versionLabel: 'ECO list project version', versionHint: 'The project that holds the ECOs.' },
];

// How a BOM row expands. Three exclusive choices stored as three separate flags —
// `use_assembly`, `use_part_hierarchy`, `use_part_instance` — in that precedence, which
// `bom_api._structure` and `settingsStore.bomStructure` both repeat. Three flags rather
// than one mode column so a row written before any of them existed still reads correctly.
const STRUCTURES = [
  { key: 'instance', label: 'Part instance', hint: 'A BOM expands into its part instances (PI filter).' },
  { key: 'hierarchy', label: 'Part hierarchy', hint: 'A part expands into its child parts, as in any BOM system (Part Catalog filter).' },
  { key: 'assembly', label: 'Assembly', hint: 'Top-level BOMs, then sub-assemblies of their own work item type, then parts as the leaves — the 3DX shape.' },
  { key: 'none', label: 'None', hint: 'Every BOM is shown without children.' },
];

// Which filters the column check asks about, and with what type and version. Only the
// filters actually in play for the chosen structure are checked — asking about a filter
// the tenant does not use would report missing columns nobody needs.
function checkSlots(form, structure) {
  const slots = [];
  const add = (kind, filterId, itemType, versionId) => {
    if (filterId && itemType) {
      slots.push({ kind, filter_id: String(filterId), item_type: String(itemType), version_id: String(versionId || '') });
    }
  };
  const asmVer = form.assembly_version_id || form.version_id;
  if (structure === 'assembly') {
    add('assembly_root', form.assembly_root_filter_id, form.assembly_root_type, asmVer);
    add('assembly', form.assembly_filter_id, form.assembly_type, asmVer);
  }
  if (structure !== 'none') add('parts', form.part_catalog_filter_id, 'PRT', form.version_id);
  add('dms', form.dms_filter_id, 'DMS', form.dms_version_id || form.version_id);
  return slots;
}

export default function BomSettingsForm() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [projects, setProjects] = useState(null);     // null = loading, [] = none/failed
  const [orcanosUrl, setOrcanosUrl] = useState('');
  // `${projectId}|${code}` -> { loading } | { available, filters, reason } | { failed }
  const [filterCache, setFilterCache] = useState({});
  const filterCacheRef = useRef({});
  // The column check: kind -> the /check-filter result, and kind -> in-flight. Run on
  // demand, never on every keystroke — each slot is a real Orcanos call.
  const [checks, setChecks] = useState({});
  const [checking, setChecking] = useState({});

  const adopt = (payload) => {
    const s = payload.settings || {};
    setInfo(payload);
    setForm({
      version_id: String(s.versionId || ''),
      bom_filter_id: String(s.bomFilterId || ''),
      instance_filter_id: String(s.instanceFilterId || ''),
      part_catalog_filter_id: String(s.partCatalogFilterId || ''),
      related_eco_filter_id: String(s.relatedEcoFilterId || ''),
      page_size: String(s.pageSize || 50),
      bom_name_prefixes: (s.bomNamePrefixes || []).join(', '),
      image_url_query: s.imageUrlQuery || '',
      use_part_instance: s.usePartInstance !== false,
      use_part_hierarchy: !!s.usePartHierarchy,
      eco_version_id: String(s.ecoVersionId || ''),
      dms_filter_id: String(s.dmsFilterId || ''),
      dms_version_id: String(s.dmsVersionId || ''),
      eco_filter_id: String(s.ecoFilterId || ''),
      eco_list_version_id: String(s.ecoListVersionId || ''),
      eco_label: s.ecoLabel || '',
      use_assembly: !!s.useAssembly,
      assembly_version_id: String(s.assemblyVersionId || ''),
      assembly_root_filter_id: String(s.assemblyRootFilterId || ''),
      assembly_root_type: s.assemblyRootType || '',
      assembly_filter_id: String(s.assemblyFilterId || ''),
      assembly_type: s.assemblyType || '',
      assembly_root_label: s.assemblyRootLabel || '',
      assembly_label: s.assemblyLabel || '',
      use_viewer_hierarchy: !!s.useViewerHierarchy,
      ps_show_assemblies: s.psShowAssemblies !== false,
      revision_column: s.revisionColumn || '',
      cost_column: s.costColumn || '',
      part_number_column: s.partNumberColumn || '',
    });
  };

  useEffect(() => {
    let cancelled = false;
    loadSettings()
      .then((payload) => { if (!cancelled) adopt(payload); })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    fetch('/api/config/projects', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setProjects(Array.isArray(d.projects) ? d.projects : []);
        setOrcanosUrl(d.orcanos_url || '');
      })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, []);

  // ECOs (CR) live in "QMS E-Forms", not the parts project — so both ECO project pickers
  // start there when they have never been set. Once per load, and only into EMPTY values:
  // a saved choice is never overwritten, and a picker the admin clears stays cleared.
  // Takes effect on Save, like any other edit.
  const ecoDefaulted = useRef(false);
  useEffect(() => {
    if (ecoDefaulted.current || !form || !projects || !info?.can_edit) return;
    ecoDefaulted.current = true;
    const flat = (name) => String(name || '').toLowerCase().replace(/[^a-z]/g, '');
    const qms = projects.find((p) => flat(p.project_name) === 'qmseforms');
    if (!qms) return;
    setForm((f) => ({
      ...f,
      eco_version_id: f.eco_version_id || String(qms.version_id),
      eco_list_version_id: f.eco_list_version_id || String(qms.version_id),
    }));
  }, [form, projects, info]);

  // One fetch per (project, type); PRT serves both the BOM and the Part Catalog fields.
  // The backend resolves the type's label from QW_Login. A transport failure is not cached,
  // so a remount can try again (same rule as the trace wizard — ConfigPage).
  const ensureFilters = useCallback((projectId, code) => {
    const key = `${projectId}|${code}`;
    if (filterCacheRef.current[key]) return;
    filterCacheRef.current[key] = { loading: true };
    setFilterCache((c) => ({ ...c, [key]: { loading: true } }));
    fetch(`/api/config/filters?project_id=${encodeURIComponent(projectId)}&item_type=${encodeURIComponent(code)}`,
      { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        const entry = { available: !!d.available, filters: d.filters || [], reason: d.reason || null };
        filterCacheRef.current[key] = entry;
        setFilterCache((c) => ({ ...c, [key]: entry }));
      })
      .catch(() => {
        delete filterCacheRef.current[key];
        setFilterCache((c) => ({ ...c, [key]: { failed: true, filters: [] } }));
      });
  }, []);

  // Run the column check for some or all slots. A failure is reported as "couldn't check",
  // NEVER dropped — silence would read as a clean bill, which is the one wrong answer here.
  const runCheck = useCallback(async (slots) => {
    if (!slots.length) return;
    setChecking((c) => ({ ...c, ...Object.fromEntries(slots.map((s) => [s.kind, true])) }));
    try {
      const resp = await fetch('/api/bom/check-filter', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ slots }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) throw new Error((data && data.detail) || `HTTP ${resp.status}`);
      setChecks((c) => ({ ...c, ...(data?.results || {}) }));
    } catch (err) {
      setChecks((c) => ({
        ...c,
        ...Object.fromEntries(slots.map((s) => [s.kind, {
          kind: s.kind, filter_id: s.filter_id, checked: false,
          reason: 'error', message: err.message,
        }])),
      }));
    } finally {
      setChecking((c) => ({ ...c, ...Object.fromEntries(slots.map((s) => [s.kind, false])) }));
    }
  }, []);

  // Check what is already configured as soon as the form opens, once. An admin who came
  // here for something else still learns that a filter lost a column — the failure this
  // whole feature exists for is one nobody goes looking for.
  const checkedOnLoad = useRef(false);
  useEffect(() => {
    if (checkedOnLoad.current || !form || !info?.can_edit) return;
    checkedOnLoad.current = true;
    const structureNow = form.use_assembly ? 'assembly'
      : form.use_part_hierarchy ? 'hierarchy'
        : form.use_part_instance ? 'instance' : 'none';
    runCheck(checkSlots(form, structureNow));
  }, [form, info, runCheck]);

  const set = (key) => (e) => {
    setSaved(false);
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };
  const setValue = (key, value) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };

  function ecoTypeLabel() {
    const ver = form.eco_list_version_id || form.version_id;
    const proj = (projects || []).find((p) => String(p.version_id) === String(ver));
    const cr = (proj?.item_types || []).find((t) => String(t.code || '').toUpperCase() === 'CR');
    return (cr?.label || '').trim();
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      const payload = await saveSettings({
        ...form,
        page_size: parseInt(form.page_size, 10) || 50,
        // The ECO list's nav entry is named after the CR work item in ITS project
        // ("Engineering Change Order"). Kept as saved when the project list didn't load.
        eco_label: ecoTypeLabel() || form.eco_label,
      });
      // A server older than this form ignores fields it doesn't know and answers 200 without
      // them — adopting that reply would silently blank what the admin just picked. Keep the
      // form as typed and say so instead.
      const out = payload.settings || {};
      const dropped = [
        ['dms_filter_id', 'dmsFilterId', 'DMS items filter'],
        ['eco_filter_id', 'ecoFilterId', 'ECO list filter'],
        ['dms_version_id', 'dmsVersionId', 'DMS project version'],
        ['eco_list_version_id', 'ecoListVersionId', 'ECO list project version'],
        ['use_part_hierarchy', 'usePartHierarchy', 'BOM structure: Part hierarchy'],
        // ⚠️ Every NEW setting belongs on this list. Pydantic ignores fields it doesn't
        // know, so a backend older than this form answers 200 without them — and adopting
        // that reply silently clears what the admin just picked (BOM_VIEWER.md §3).
        ['use_assembly', 'useAssembly', 'BOM structure: Assembly'],
        ['assembly_version_id', 'assemblyVersionId', 'Assembly project version'],
        ['assembly_root_filter_id', 'assemblyRootFilterId', 'Top-level BOM filter'],
        ['assembly_root_type', 'assemblyRootType', 'Top-level BOM work item'],
        ['assembly_filter_id', 'assemblyFilterId', 'Sub-assembly filter'],
        ['assembly_type', 'assemblyType', 'Assembly work item'],
        ['use_viewer_hierarchy', 'useViewerHierarchy', 'Product structures in the BOM Viewer'],
        ['ps_show_assemblies', 'psShowAssemblies', 'Show the Assembly list in the menu'],
        ['revision_column', 'revisionColumn', 'Revision column'],
        ['cost_column', 'costColumn', 'Unit cost column'],
        ['part_number_column', 'partNumberColumn', 'Part number column'],
      ].filter(([k, outKey]) => form[k] && String(out[outKey] ?? '') !== String(form[k]))
        .map(([, , label]) => label);
      if (dropped.length) {
        setError(`The server did not keep: ${dropped.join(', ')}. It is probably running an older `
          + 'version — restart the backend and save again.');
        return;
      }
      adopt(payload);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return <div className="settings-card"><div className="settings-body settings-error" role="alert">{loadError}</div></div>;
  }
  if (!form) {
    return <div className="settings-card"><div className="settings-body">Loading…</div></div>;
  }

  const canEdit = !!info.can_edit;
  // Three stored flags, one choice. Precedence assembly > hierarchy > instance, the same
  // order `bom_api._structure` and `settingsStore.bomStructure` apply — a fourth copy of
  // this rule that disagrees is a tree that expands through a filter nobody configured.
  const structure = form.use_assembly ? 'assembly'
    : form.use_part_hierarchy ? 'hierarchy'
      : form.use_part_instance ? 'instance' : 'none';
  const setStructure = (key) => {
    setSaved(false);
    setForm((f) => ({
      ...f,
      use_assembly: key === 'assembly',
      use_part_hierarchy: key === 'hierarchy',
      use_part_instance: key === 'instance',
    }));
  };
  const baseUrl = info.settings?.baseUrl || '';
  const projectOf = (versionId) => (projects || []).find((p) => String(p.version_id) === String(versionId));
  // The version a filter is read from: its own project when it has one, else the parts
  // project. The sub-assembly filter has no picker of its own — it shares the one the
  // top-level BOM filter carries, because both types live in the same project.
  const versionFor = (f) => (f.assemblyVersion && form.assembly_version_id)
    || (f.versionKey && form[f.versionKey])
    || form.version_id;
  // A filter's work item type: fixed (`code`) for the PRT/PI/DMS/CR filters, chosen by the
  // admin for the two assembly ones.
  const codeFor = (f) => (f.typeKey ? (form[f.typeKey] || '') : f.code);
  const itemTypesOf = (versionId) => (projectOf(versionId)?.item_types || []);
  const visibleFilters = FILTERS.filter((f) => !f.only || f.only.includes(structure));

  return (
    <form className="settings-card" onSubmit={handleSave}>
      <div className="settings-header">
        <h2>BOM Viewer</h2>
        <span className="settings-meta">
          Tenant <strong>{info.tenant || '—'}</strong>
          {info.updated_at && ` · last saved ${info.updated_at}${info.updated_by ? ` by ${info.updated_by}` : ''}`}
        </span>
      </div>

      <div className="settings-note">
        {canEdit
          ? 'The Orcanos project and saved filters this tenant\'s BOM viewer reads.'
          : 'Only an Orcanos administrator can change these settings.'}
      </div>

      <div className="settings-body">
        <Field label="Orcanos URL" value={baseUrl} mono />

        <Row label="Project version" required>
          <ProjectPicker
            projects={projects}
            versionId={form.version_id}
            disabled={!canEdit}
            onChange={(v) => setValue('version_id', v)}
          />
        </Row>

        <div className="settings-field bomset-field-top">
          <div className="settings-field-label">BOM structure</div>
          <div className="bomset-radios" role="radiogroup" aria-label="BOM structure">
            {STRUCTURES.map((o) => (
              <label key={o.key} className="bomset-check">
                <input
                  type="radio"
                  name="bom_structure"
                  checked={structure === o.key}
                  onChange={() => setStructure(o.key)}
                  disabled={!canEdit}
                />
                <strong>{o.label}</strong>
                <div className="settings-hint">{o.hint}</div>
              </label>
            ))}
          </div>
        </div>

        {visibleFilters.map((f) => (
          <React.Fragment key={f.key}>
          {f.versionKey && (
            <Row label={f.versionLabel}>
              <ProjectPicker
                projects={projects}
                versionId={versionFor(f)}
                disabled={!canEdit}
                onChange={(v) => setValue(f.versionKey, v)}
              />
              <div className="settings-hint">{f.versionHint}</div>
            </Row>
          )}
          {/* The assembly filters carry their own work item type — the tenant's BOM type
              and its Assembly type. A CODE is stored (PRT, ASS, …), never the label: a
              label belongs to one project and is renamed there. The label is stored
              alongside it only so the UI can name the type it picked. */}
          {f.typeKey && (
            <Row label={f.typeLabel} required={f.required}>
              <ItemTypePicker
                itemTypes={itemTypesOf(versionFor(f))}
                value={form[f.typeKey]}
                disabled={!canEdit}
                onChange={(code, label) => {
                  setValue(f.typeKey, code);
                  if (f.labelKey) setValue(f.labelKey, label || '');
                }}
              />
            </Row>
          )}
          <Row label={f.label} required={f.required}>
            <FilterPicker
              projectId={projectOf(versionFor(f))?.project_id}
              code={codeFor(f)}
              value={form[f.key]}
              onChange={(v) => setValue(f.key, v)}
              disabled={!canEdit}
              required={f.required}
              cache={filterCache}
              ensure={ensureFilters}
            />
            <div className="settings-hint">
              {(structure === 'hierarchy' && f.hierarchyHint)
                || (structure === 'assembly' && f.assemblyHint)
                || f.hint}
            </div>
            {structure === 'hierarchy' && f.hierarchyHint && !form[f.key] && (
              <div className="settings-hint bomset-warn">Part hierarchy reads children from this filter — without it no BOM expands.</div>
            )}
            {structure === 'assembly' && f.key === 'part_catalog_filter_id' && !form[f.key] && (
              <div className="settings-hint bomset-warn">The assembly structure reads its leaves from this filter — without it an assembly shows sub-assemblies but no parts.</div>
            )}
            <OpenFilterLink
              baseUrl={orcanosUrl}
              versionId={versionFor(f)}
              itemTypeCode={codeFor(f)}
              filterId={form[f.key]}
              label={f.label}
            />
            {/* Does the filter SELECT what we read from it? Advisory — never blocks Save. */}
            {f.check && (
              <BomFilterCheck
                result={checks[f.check]}
                busy={!!checking[f.check]}
                hasFilter={!!form[f.key] && !!codeFor(f)}
                onRecheck={() => runCheck(checkSlots(form, structure).filter((s) => s.kind === f.check))}
              />
            )}
          </Row>
          </React.Fragment>
        ))}

        {/* Product structures (2026-10-28): the hierarchy kept in the BOM Viewer. Its own switch,
            NOT a fourth radio above — the Orcanos structure and its precedence (Note #61) stay as they are. */}
        <div className="settings-field bomset-field-top">
          <div className="settings-field-label">Product structures</div>
          <div>
            <label className="bomset-check">
              <input
                type="checkbox"
                checked={!!form.use_viewer_hierarchy}
                onChange={(e) => setValue('use_viewer_hierarchy', e.target.checked)}
                disabled={!canEdit}
              />
              <strong>Keep product structures in the BOM Viewer</strong>
              <div className="settings-hint">Parts stay in Orcanos (Part Catalog and DMS items filters). Parent → child,
                quantities, baselines and branches are kept here, so one sub-assembly can be used in many products with a
                different quantity in each. Adds Dashboard and Product structures at the top of the BOM menu, and Part usage, Baselines and Branches at the end;
                BOMs leaves the menu. The BOM structure above is not changed.</div>
            </label>
            {form.use_viewer_hierarchy && (
              <label className="bomset-check">
                <input
                  type="checkbox"
                  checked={form.ps_show_assemblies !== false}
                  onChange={(e) => setValue('ps_show_assemblies', e.target.checked)}
                  disabled={!canEdit}
                />
                <strong>Show the {form.assembly_label || 'Assembly'} list in the menu</strong>
                <div className="settings-hint">The Orcanos list of {form.assembly_label || 'Assembly'} items, beside Product structures.
                  {form.assembly_filter_id ? ' Works with any BOM structure above.'
                    : ' Needs a Sub-assembly filter: pick BOM structure = Assembly once to set it.'}</div>
              </label>
            )}
          </div>
        </div>
        {form.use_viewer_hierarchy && (
          <>
            <Input label="Revision column" value={form.revision_column} onChange={set('revision_column')} disabled={!canEdit}
              hint="Title of the Part Catalog / DMS filter column that holds the revision (e.g. Revision). Empty = Revision / Rev / Version is looked for. A baseline records it." />
            <Input label="Unit cost column" value={form.cost_column} onChange={set('cost_column')} disabled={!canEdit}
              hint="Title of the column that holds the unit cost. Empty = Unit Cost / Cost / Price is looked for. Used for the rolled-up cost." />
            <Input label="Part number column" value={form.part_number_column} onChange={set('part_number_column')} disabled={!canEdit}
              hint="Title of the Part Catalog filter column that holds the part number. Import BOM file matches each line on it, and writes it on a new part. Empty = the part's Name (and its key) is matched, and a new part gets the part number as its Name." />
          </>
        )}

        <Input label="Page size" value={form.page_size} onChange={set('page_size')} disabled={!canEdit} numeric />
        <Input label="BOM name prefixes" value={form.bom_name_prefixes} onChange={set('bom_name_prefixes')} disabled={!canEdit} hint="Comma-separated. Marks a Part Catalog item as a BOM." />
        <Input
          label="Image URL token"
          value={form.image_url_query}
          onChange={set('image_url_query')}
          disabled={!canEdit}
          mono
          hint="Query appended to images stored in Orcanos (e.g. q=abc-123), so they load without an Orcanos session. Leave empty if this tenant's images are all external links."
        />

        {error && <div className="settings-error" role="alert">{error}</div>}
      </div>

      {canEdit && (
        <div className="settings-footer">
          {saved && <span className="settings-saved">Saved</span>}
          {/* Advisory, not a gate — Save is never disabled by the check. */}
          <button
            type="button"
            className="bomfc-link"
            onClick={() => runCheck(checkSlots(form, structure))}
            disabled={Object.values(checking).some(Boolean)}
          >
            Check filter columns
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </form>
  );
}

/** Project + version from QW_Login. Stores the version id; a saved id this user can't see
 *  stays selectable as "#id (current)" so a save never silently changes it. */
function ProjectPicker({ projects, versionId, disabled, onChange }) {
  if (projects === null) return <div className="settings-hint">Loading projects…</div>;
  if (projects.length === 0) {
    return (
      <>
        <input className="settings-input" value={versionId} onChange={(e) => onChange(e.target.value)}
          disabled={disabled} required inputMode="numeric" pattern="\d*" />
        <div className="settings-hint">Couldn&rsquo;t load the project list — enter the version id.</div>
      </>
    );
  }
  const byLabel = {};
  const options = [''];
  let current = '';
  projects.forEach((p) => {
    const l = projectLabel(p);
    byLabel[l] = String(p.version_id);
    options.push(l);
    if (String(p.version_id) === String(versionId)) current = l;
  });
  if (versionId && !current) {
    current = `#${versionId} (current)`;
    byLabel[current] = String(versionId);
    options.splice(1, 0, current);
  }
  return (
    <SearchableSelect
      options={options}
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(byLabel[e.target.value] ?? '')}
      placeholder="Search projects..."
    />
  );
}

/** A saved-filter dropdown for one item type, or a plain id box when there is no list. */
function FilterPicker({ projectId, code, value, onChange, disabled, required, cache, ensure }) {
  useEffect(() => {
    if (projectId && code) ensure(projectId, code);
  }, [projectId, code, ensure]);

  const entry = projectId ? cache[`${projectId}|${code}`] : null;

  if (!entry || !entry.available || !entry.filters.length) {
    return (
      <>
        <input className="settings-input" value={value} onChange={(e) => onChange(e.target.value)}
          disabled={disabled} required={required} inputMode="numeric" pattern="\d*"
          placeholder={entry?.loading ? 'Loading filters…' : 'Filter id'} />
        {entry?.failed && (
          <div className="settings-hint bomset-warn">Couldn&rsquo;t load the filter list — reload to try again, or enter the filter id.</div>
        )}
        {entry && !entry.failed && !entry.loading && entry.reason === 'unsupported_type' && (
          <div className="settings-hint">Orcanos keeps no saved filters for {code} — enter the filter id.</div>
        )}
      </>
    );
  }

  const byLabel = {};
  const options = [''];
  let current = '';
  entry.filters.forEach((f) => {
    const l = `${f.name} (#${f.id})`;
    byLabel[l] = String(f.id);
    options.push(l);
    if (value && String(f.id) === String(value)) current = l;
  });
  if (value && !current) {
    current = `#${value} (current)`;
    byLabel[current] = String(value);
    options.splice(1, 0, current);
  }
  return (
    <SearchableSelect
      options={options}
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(byLabel[e.target.value] ?? '')}
      placeholder="Search filters..."
    />
  );
}

/** The work item types of one project, from QW_Login. Stores the CODE; a saved code this
 *  project doesn't list stays selectable as "CODE (current)", so opening the form on a
 *  different project never silently drops the type the tenant is actually running on. */
function ItemTypePicker({ itemTypes, value, disabled, onChange }) {
  if (!itemTypes || itemTypes.length === 0) {
    return (
      <>
        <input className="settings-input mono" value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase(), '')}
          disabled={disabled} placeholder="Work item type code" />
        <div className="settings-hint">
          Couldn&rsquo;t read this project&rsquo;s work items — enter the type code (e.g. ASS).
        </div>
      </>
    );
  }
  const byLabel = {};
  const options = [''];
  let current = '';
  itemTypes.forEach((t) => {
    const code = String(t.code || '').toUpperCase();
    if (!code) return;
    const l = `${t.label || code} (${code})`;
    byLabel[l] = { code, label: (t.label || '').trim() };
    options.push(l);
    if (code === String(value || '').toUpperCase()) current = l;
  });
  if (value && !current) {
    current = `${value} (current)`;
    byLabel[current] = { code: String(value).toUpperCase(), label: '' };
    options.splice(1, 0, current);
  }
  return (
    <SearchableSelect
      options={options}
      value={current}
      disabled={disabled}
      onChange={(e) => {
        const hit = byLabel[e.target.value];
        onChange(hit ? hit.code : '', hit ? hit.label : '');
      }}
      placeholder="Search work items..."
    />
  );
}

function Row({ label, required, children }) {
  return (
    <div className="settings-field">
      <div className="settings-field-label">
        {label}{required && <span className="settings-required"> *</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div className="settings-field">
      <div className="settings-field-label">{label}</div>
      <div className={'settings-field-value' + (mono ? ' mono' : '')}>
        {value || <span className="settings-field-empty">—</span>}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, disabled, required, numeric, mono, hint }) {
  return (
    <label className="settings-field">
      <div className="settings-field-label">
        {label}{required && <span className="settings-required"> *</span>}
      </div>
      <div>
        <input
          className={'settings-input' + (mono ? ' mono' : '')}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          inputMode={numeric ? 'numeric' : undefined}
          pattern={numeric ? '\\d*' : undefined}
        />
        {hint && <div className="settings-hint">{hint}</div>}
      </div>
    </label>
  );
}
